import axios from 'axios'
import api from 'src/services/APIInterceptor'
import { throwIfAborted } from './sharedRequest'

let cooldownUntil = 0

export const getMapRouteCacheKey = (from, to, profile = 'primary') =>
  JSON.stringify([
    process.env.GATSBY_API_URL,
    'maps/route:v1',
    from,
    to,
    profile
  ])

const routeError = (status, code) => {
  const error = new Error('Map route request failed')
  error.status = status
  error.code = code
  return error
}

const getRouteDetails = route => {
  const coordinates = route?.geometry?.coordinates
  if (
    route?.geometry?.type !== 'LineString' ||
    !Array.isArray(coordinates) ||
    coordinates.length < 2 ||
    !coordinates.every(
      point =>
        Array.isArray(point) &&
        point.length === 2 &&
        point.every(Number.isFinite) &&
        Math.abs(point[0]) <= 180 &&
        Math.abs(point[1]) <= 90
    ) ||
    !Number.isFinite(route.distanceM) ||
    route.distanceM < 0 ||
    !Number.isFinite(route.durationS) ||
    route.durationS < 0
  ) {
    throw routeError(502, 50210)
  }

  return {
    coordinates,
    distanceKm: route.distanceM / 1000,
    durationSeconds: route.durationS,
    notices: route.notices || [],
    legs: route.legs || []
  }
}

export const getMapRouteDetails = async (
  from,
  to,
  signal,
  profile = 'primary'
) => {
  throwIfAborted(signal)
  if (Date.now() < cooldownUntil) throw routeError(429, 42900)

  const source = axios.CancelToken.source()
  const cancel = () => source.cancel('Route request was cancelled')
  if (signal) signal.addEventListener('abort', cancel, { once: true })
  try {
    // The shared UI client supplies session cookies and unwraps/camel-cases data.
    const { data } = await api.post(
      '/maps/route',
      {
        locations: [
          { lat: from[1], lon: from[0] },
          { lat: to[1], lon: to[0] }
        ],
        alternatives: profile === 'alternatives' ? 2 : 0
      },
      { cancelToken: source.token, skipGlobalValidationError: true }
    )
    throwIfAborted(signal)
    const routes = data?.routes
    if (!Array.isArray(routes) || routes[0]?.kind !== 'primary') {
      throw routeError(502, 50210)
    }
    const common = {
      waypoints: data.waypoints || [],
      attributions: data.meta?.attributions || []
    }
    const normalize = route => {
      const details = getRouteDetails(route)
      return {
        ...details,
        ...common,
        notices: [...(data.notices || []), ...details.notices]
      }
    }
    return {
      ...normalize(routes[0]),
      alternates:
        profile === 'alternatives' ? routes.slice(1).map(normalize) : []
    }
  } catch (failure) {
    throwIfAborted(signal)
    const status = failure.status
    const code = failure.data?.code || failure.code
    if (status === 429) {
      const retryAfter =
        failure.headers?.retryAfter || failure.headers?.['retry-after']
      const seconds = Number(retryAfter)
      const retryAt =
        retryAfter && Number.isFinite(seconds)
          ? Date.now() + Math.max(0, seconds) * 1000
          : Date.parse(retryAfter)
      cooldownUntil = Math.max(Date.now() + 1000, retryAt || Date.now() + 60000)
    }
    const error = routeError(status, code)
    error.errors = failure.data?.errors
    throw error
  } finally {
    if (signal) signal.removeEventListener('abort', cancel)
  }
}
